import {
    Box, Sheet, Stack, Typography, CircularProgress, Button,
    Divider, IconButton, Input, Textarea, Tooltip, Chip,
} from "@mui/joy";
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

import RefreshRoundedIcon                 from "@mui/icons-material/RefreshRounded";
import LockRoundedIcon                    from "@mui/icons-material/LockRounded";
import AssignmentRoundedIcon              from "@mui/icons-material/AssignmentRounded";
import NavigateNextRoundedIcon            from "@mui/icons-material/NavigateNextRounded";
import CheckBoxRoundedIcon                from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon    from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import RadioButtonUncheckedRoundedIcon    from "@mui/icons-material/RadioButtonUncheckedRounded";
import RadioButtonCheckedRoundedIcon      from "@mui/icons-material/RadioButtonCheckedRounded";
import SendRoundedIcon                    from "@mui/icons-material/SendRounded";
import FormatQuoteRoundedIcon             from "@mui/icons-material/FormatQuoteRounded";
import LocalFireDepartmentRoundedIcon     from "@mui/icons-material/LocalFireDepartmentRounded";
import AddRoundedIcon                     from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon                  from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon                    from "@mui/icons-material/EditRounded";
import CheckRoundedIcon                   from "@mui/icons-material/CheckRounded";
import SubdirectoryArrowRightRoundedIcon  from "@mui/icons-material/SubdirectoryArrowRightRounded";
import FormatListBulletedRoundedIcon      from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon      from "@mui/icons-material/FormatListNumberedRounded";
import DriveFileRenameOutlineRoundedIcon  from "@mui/icons-material/DriveFileRenameOutlineRounded";
import MoreHorizRoundedIcon               from "@mui/icons-material/MoreHorizRounded";
import ViewListRoundedIcon                from "@mui/icons-material/ViewListRounded";
import CloseRoundedIcon                   from "@mui/icons-material/CloseRounded";
import FormatBoldRoundedIcon   from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded";
import TableChartRoundedIcon        from "@mui/icons-material/TableChartRounded";
import ImageRoundedIcon             from "@mui/icons-material/ImageRounded";
import AttachFileRoundedIcon        from "@mui/icons-material/AttachFileRounded";
import DownloadRoundedIcon          from "@mui/icons-material/DownloadRounded";
import InsertDriveFileRoundedIcon   from "@mui/icons-material/InsertDriveFileRounded";
import { keyframes } from "@mui/system";
import ImageViewer from "../components/ImageViewer";

import { BASE_URL, APICheckShareLink, APIGetShareData, mediaUrl } from "../api";

// ─── Guest API helper ─────────────────────────────────────────────────────────
// Все мутирующие запросы идут с заголовком X-Share-Token вместо Bearer

function makeGuestApi(shareToken) {
    return axios.create({
        baseURL: BASE_URL,
        headers: {
            "Content-Type": "application/json",
            "X-Share-Token": shareToken,
        },
    });
}

// ─── Constants ────────────────────────────────────────────────────────────────

const FLAME_OPTIONS = [
    { value: null,     label: "Без огонька", color: "#ccc" },
    { value: "yellow", label: "Важно",       color: "#f59e0b" },
    { value: "red",    label: "Срочно",      color: "#ef4444" },
];

// ─── «Горящая» плашка важной задачи ───────────────────────────────────────────
const flameGlowRed = keyframes`
    0%, 100% { background-position: 0% 50%;   box-shadow: 0 0 0 rgba(239,68,68,0); }
    50%      { background-position: 100% 50%; box-shadow: 0 0 12px rgba(239,68,68,0.28); }
`;
const flameGlowYellow = keyframes`
    0%, 100% { background-position: 0% 50%;   box-shadow: 0 0 0 rgba(245,158,11,0); }
    50%      { background-position: 100% 50%; box-shadow: 0 0 10px rgba(245,158,11,0.24); }
`;
function flameRowSx(flame) {
    if (flame === "red") return {
        border: "1px solid rgba(239,68,68,0.35)",
        backgroundImage: "linear-gradient(100deg, rgba(239,68,68,0.10), rgba(249,115,22,0.13), rgba(239,68,68,0.10))",
        backgroundSize: "200% 100%",
        animation: `${flameGlowRed} 3.2s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none", backgroundPosition: "50% 50%" },
    };
    if (flame === "yellow") return {
        border: "1px solid rgba(245,158,11,0.32)",
        backgroundImage: "linear-gradient(100deg, rgba(245,158,11,0.10), rgba(250,204,21,0.15), rgba(245,158,11,0.10))",
        backgroundSize: "200% 100%",
        animation: `${flameGlowYellow} 3.6s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none", backgroundPosition: "50% 50%" },
    };
    return {};
}

// Размер файла человекочитаемо + лимит вложения (нужны редактору и бабблу)
const MAX_ATTACH_SIZE = 25 * 1024 * 1024;
function fmtSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

const ITEM_TYPES = [
    { value: "checkbox", label: "Чекбокс",    icon: <CheckBoxOutlineBlankRoundedIcon sx={{ fontSize: 15 }} /> },
    { value: "radio",    label: "Радиокнопка", icon: <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 15 }} /> },
    { value: "bullet",   label: "Маркер",      icon: <FormatListBulletedRoundedIcon   sx={{ fontSize: 15 }} /> },
    { value: "numbered", label: "Нумерация",   icon: <FormatListNumberedRoundedIcon   sx={{ fontSize: 15 }} /> },
    { value: "text",     label: "Текст",       icon: <DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 15 }} /> },
];

const COUNTABLE_TYPES = ["checkbox", "radio"];

function collectCountable(items) {
    const result = [];
    for (const item of items) {
        if (COUNTABLE_TYPES.includes(item.type)) result.push(item);
        if (item.subtasks?.length) result.push(...collectCountable(item.subtasks));
    }
    return result;
}

let _nextTmp = 1;
const genTmpId = () => `tmp-guest-${_nextTmp++}`;
let _nextStableKey = 1;
const genStableKey = () => `sk-${_nextStableKey++}`;

const fmtNow = () => {
    const d = new Date();
    return `${d.getDate()} ${["янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек"][d.getMonth()]}, ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

// ─── Normalisers (same shape as TasksPage) ───────────────────────────────────

function normaliseComment(raw) {
    return {
        id:        raw.id,
        text:      raw.text ?? "",
        quoted:    raw.is_quoted ?? false,
        createdAt: raw.created_at ?? fmtNow(),
        updatedAt: raw.updated_at ?? null,
        authorName: raw.author_name ?? null,
        attachments: (raw.attachments ?? []).map(a => ({
            id: a.id, url: a.url, kind: a.kind,
            name: a.original_name ?? "", mime: a.mime_type ?? "", size: a.size ?? 0,
        })),
    };
}
function normaliseItem(raw) {
    return {
        id:       raw.id,
        type:     raw.item_type ?? "checkbox",
        text:     raw.text ?? "",
        done:     raw.done ?? false,
        flame:    raw.flame || null,
        expanded: raw.is_expanded ?? false,
        comments: (raw.comments ?? []).map(normaliseComment),
        subtasks: (raw.subtasks  ?? []).map(normaliseItem),
    };
}
function normaliseSection(raw) {
    return {
        id:    raw.id,
        title: raw.title ?? "",
        items: (raw.items ?? []).map(normaliseItem),
    };
}
function normaliseTaskList(raw) {
    return {
        id:       raw.id,
        name:     raw.name ?? "Список",
        priority: raw.priority ?? "medium",
        sections: (raw.sections ?? []).map(normaliseSection),
    };
}

// ─── Recursive item helpers ───────────────────────────────────────────────────

function updateItemRec(items, id, fn) {
    return items.map(it => {
        if (it.id === id) return fn(it);
        if (it.subtasks?.length) return { ...it, subtasks: updateItemRec(it.subtasks, id, fn) };
        return it;
    });
}
function deleteItemRec(items, id) {
    return items
        .filter(it => it.id !== id)
        .map(it => it.subtasks?.length ? { ...it, subtasks: deleteItemRec(it.subtasks, id) } : it);
}
function addSubtaskRec(items, parentId, newItem) {
    return items.map(it => {
        if (it.id === parentId) return { ...it, subtasks: [...(it.subtasks || []), newItem], expanded: true };
        if (it.subtasks?.length) return { ...it, subtasks: addSubtaskRec(it.subtasks, parentId, newItem) };
        return it;
    });
}
function replaceItemId(items, tmpId, realId) {
    return items.map(it => {
        const base = it.id === tmpId ? { ...it, id: realId } : it;
        if (base.subtasks?.length) return { ...base, subtasks: replaceItemId(base.subtasks, tmpId, realId) };
        return base;
    });
}

// ─── ItemIcon ─────────────────────────────────────────────────────────────────

function ItemIcon({ type, done, index, onToggle, canEdit }) {
    const base = { fontSize: 20, flexShrink: 0, cursor: canEdit ? "pointer" : "default" };
    if (type === "checkbox") return done
        ? <CheckBoxRoundedIcon sx={{ ...base, color: "primary.400" }} onClick={canEdit ? onToggle : undefined} />
        : <CheckBoxOutlineBlankRoundedIcon sx={{ ...base, color: "text.tertiary" }} onClick={canEdit ? onToggle : undefined} />;
    if (type === "radio") return done
        ? <RadioButtonCheckedRoundedIcon sx={{ ...base, color: "primary.400" }} onClick={canEdit ? onToggle : undefined} />
        : <RadioButtonUncheckedRoundedIcon sx={{ ...base, color: "text.tertiary" }} onClick={canEdit ? onToggle : undefined} />;
    if (type === "bullet") return (
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "primary.400", flexShrink: 0 }} />
    );
    if (type === "numbered") return (
        <Typography sx={{ fontSize: "0.8rem", color: "primary.400", fontWeight: 700, minWidth: 18, flexShrink: 0, lineHeight: 1 }}>
            {(index ?? 0) + 1}.
        </Typography>
    );
    return <ViewListRoundedIcon sx={{ fontSize: 14, color: "text.tertiary", flexShrink: 0 }} />;
}

// ─── FlameMenu ────────────────────────────────────────────────────────────────

function FlameMenu({ flame, onChange }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos]   = useState({ top: 0, left: 0 });
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);
    const flameColor = flame === "red" ? "#ef4444" : flame === "yellow" ? "#f59e0b" : null;
    return (
        <Box ref={ref} sx={{ position: "relative" }}>
            <Tooltip title="Срочность" size="sm">
                <IconButton size="sm" variant="plain"
                    sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7, color: flameColor || "text.tertiary" }}
                    onMouseDown={e => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setPos({ top: rect.bottom + 4, left: rect.right - 140 });
                        setOpen(v => !v);
                    }}
                >
                    <LocalFireDepartmentRoundedIcon sx={{ fontSize: 15, color: flameColor || "inherit" }} />
                </IconButton>
            </Tooltip>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.95 }} transition={{ duration: 0.12 }}
                        style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999,
                            background: "white", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.14)", padding: "6px", minWidth: 140 }}
                    >
                        {FLAME_OPTIONS.map(opt => (
                            <Box key={String(opt.value)}
                                onMouseDown={e => { e.stopPropagation(); onChange(opt.value); setOpen(false); }}
                                sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.2, py: 0.65, borderRadius: 8, cursor: "pointer",
                                    bgcolor: flame === opt.value ? "primary.softBg" : "transparent", "&:hover": { bgcolor: "neutral.softBg" } }}
                            >
                                <LocalFireDepartmentRoundedIcon sx={{ fontSize: 15, color: opt.color }} />
                                <Typography sx={{ fontSize: "0.82rem" }}>{opt.label}</Typography>
                            </Box>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

// ─── TypeMenu ─────────────────────────────────────────────────────────────────

function TypeMenu({ type, onChange }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos]   = useState({ top: 0, left: 0 });
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);
    return (
        <Box ref={ref} sx={{ position: "relative" }}>
            <Tooltip title="Тип элемента" size="sm">
                <IconButton size="sm" variant="plain" color="neutral"
                    sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                    onMouseDown={e => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setPos({ top: rect.bottom + 4, left: rect.right - 140 });
                        setOpen(v => !v);
                    }}
                >
                    <MoreHorizRoundedIcon sx={{ fontSize: 15 }} />
                </IconButton>
            </Tooltip>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.95 }} transition={{ duration: 0.12 }}
                        style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999,
                            background: "white", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.14)", padding: "6px", minWidth: 140 }}
                    >
                        {ITEM_TYPES.map(t => (
                            <Box key={t.value}
                                onMouseDown={e => { e.stopPropagation(); onChange(t.value); setOpen(false); }}
                                sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.2, py: 0.65, borderRadius: 8, cursor: "pointer",
                                    bgcolor: type === t.value ? "primary.softBg" : "transparent", "&:hover": { bgcolor: "neutral.softBg" } }}
                            >
                                <Box sx={{ opacity: 0.7, display: "flex" }}>{t.icon}</Box>
                                <Typography sx={{ fontSize: "0.82rem" }}>{t.label}</Typography>
                            </Box>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

// ─── CommentBox — блок добавления/просмотра комментариев ─────────────────────

function CommentBox({ item, shareToken, canComment, canEdit, onCommentAdded, onCommentDeleted }) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
        if (!text.trim()) return;
        setSending(true);
        try {
            const api = makeGuestApi(shareToken);
            const res = await api.post(`/tasks/items/${item.id}/comments/`, { text: text.trim() });
            onCommentAdded(item.id, normaliseComment(res.data));
            setText("");
        } catch (e) {
            console.error("Ошибка комментария:", e);
        } finally {
            setSending(false);
        }
    };

    const handleDelete = async (commentId) => {
        try {
            const api = makeGuestApi(shareToken);
            await api.delete(`/tasks/comments/${commentId}/`);
            onCommentDeleted(item.id, commentId);
        } catch (e) {
            console.error("Ошибка удаления комментария:", e);
        }
    };

    return (
        <Box sx={{ pl: "46px", pr: 1, pb: 1 }}>
            {/* Существующие комментарии */}
            {(item.comments || []).map(c => (
                <Box key={c.id} className="comment-bubble"
                    sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, my: 0.4, position: "relative" }}
                >
                    <Box sx={{
                        flex: 1, px: 1.5, py: 0.7,
                        bgcolor: "primary.50", borderRadius: 10,
                        borderLeft: "3px solid", borderColor: "primary.300",
                    }}>
                        <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                            {c.text.replace(/<[^>]+>/g, "")}
                        </Typography>
                        {c.createdAt && (
                            <Typography sx={{ fontSize: "0.7rem", color: "text.tertiary", mt: 0.3 }}>
                                {c.createdAt}
                            </Typography>
                        )}
                    </Box>
                    {canEdit && (
                        <IconButton size="sm" variant="plain" color="danger"
                            sx={{ width: 22, height: 22, minWidth: 0, borderRadius: 6, opacity: 0.5, "&:hover": { opacity: 1 } }}
                            onClick={() => handleDelete(c.id)}
                        >
                            <DeleteRoundedIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    )}
                </Box>
            ))}

            {/* Поле ввода нового комментария */}
            {canComment && (
                <Box sx={{ display: "flex", gap: 0.75, mt: 0.5, alignItems: "flex-end" }}>
                    <Textarea
                        value={text}
                        onChange={e => setText(e.target.value)}
                        placeholder="Написать комментарий..."
                        minRows={1} maxRows={4}
                        onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                        }}
                        sx={{ flex: 1, fontSize: "0.85rem", "--Textarea-focusedHighlight": "var(--joy-palette-primary-300)" }}
                    />
                    <IconButton
                        size="sm" variant="solid" color="primary"
                        disabled={!text.trim() || sending}
                        onClick={handleSend}
                        sx={{ width: 32, height: 32, minWidth: 0, borderRadius: 9, mb: 0.2 }}
                    >
                        {sending ? <CircularProgress size="sm" sx={{ "--CircularProgress-size": "14px" }} /> : <SendRoundedIcon sx={{ fontSize: 15 }} />}
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}


function GuestItemMobileMenu({ item, accessLevel, depth, onPatch, onDelete, onAddSubtask, onCommentToggle }) {
    const canEdit    = accessLevel === "edit";
    const canComment = accessLevel === "comment" || accessLevel === "edit";
    const [open, setOpen] = useState(false);
    const [pos, setPos]   = useState({ top: 0, left: 0 });
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);

    const actions = [
        ...(canComment ? [{
            label: "Комментарий",
            icon: <FormatQuoteRoundedIcon sx={{ fontSize: 15 }} />,
            onClick: () => onCommentToggle(),
        }] : []),
        ...(canEdit ? [{
            label: "Редактировать",
            icon: <EditRoundedIcon sx={{ fontSize: 15 }} />,
            onClick: () => onPatch(item.id, { _startEdit: true }),
        }] : []),
        ...(canEdit && depth === 0 ? [{
            label: "Подзадача",
            icon: <SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 15 }} />,
            onClick: () => onAddSubtask(item.id),
        }] : []),
        ...(canEdit ? [
            {
                label: item.flame === "red" ? "Убрать срочно" : "Срочно 🔴",
                icon: <LocalFireDepartmentRoundedIcon sx={{ fontSize: 15, color: "#ef4444" }} />,
                onClick: () => onPatch(item.id, { flame: item.flame === "red" ? null : "red" }),
            },
            {
                label: item.flame === "yellow" ? "Убрать важность" : "Важно 🟡",
                icon: <LocalFireDepartmentRoundedIcon sx={{ fontSize: 15, color: "#f59e0b" }} />,
                onClick: () => onPatch(item.id, { flame: item.flame === "yellow" ? null : "yellow" }),
            },
            {
                label: "Удалить",
                icon: <DeleteRoundedIcon sx={{ fontSize: 15 }} />,
                onClick: () => { if (window.confirm("Удалить задачу?")) onDelete(item.id); },
                danger: true,
            },
        ] : []),
    ];

    if (actions.length === 0) return null;

    return (
        <Box ref={ref} sx={{ position: "relative", flexShrink: 0, display: { xs: "flex", md: "none" } }}>
            <IconButton size="sm" variant="plain" color="neutral"
                sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7, opacity: 0.5 }}
                onMouseDown={e => {
                    e.stopPropagation();
                    const rect    = e.currentTarget.getBoundingClientRect();
                    const menuH   = actions.length * 42 + 16;
                    const menuW   = 190;
                    const screenH = window.innerHeight;
                    const screenW = window.innerWidth;
                    const top  = rect.bottom + menuH > screenH ? rect.top - menuH - 4 : rect.bottom + 4;
                    const left = rect.left + menuW > screenW  ? screenW - menuW - 8   : rect.left;
                    setPos({ top: Math.max(8, top), left: Math.max(8, left) });
                    setOpen(v => !v);
                }}
            >
                <MoreHorizRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: -4 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: -4 }}
                        transition={{ duration: 0.12 }}
                        style={{
                            position: "fixed", top: pos.top, left: pos.left, zIndex: 9999,
                            background: "var(--joy-palette-background-surface)",
                            borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.13)",
                            border: "1px solid var(--joy-palette-neutral-200)",
                            padding: "4px", minWidth: 190,
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {actions.map((a, i) => (
                            <Box key={i}
                                onClick={() => { a.onClick(); setOpen(false); }}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1.5,
                                    px: 1.5, py: 0.9, borderRadius: 8, cursor: "pointer",
                                    color: a.danger ? "danger.plainColor" : "text.primary",
                                    "&:hover": { bgcolor: a.danger ? "danger.softBg" : "neutral.softBg" },
                                    transition: "background 0.1s",
                                }}
                            >
                                <Box sx={{ display: "flex", opacity: a.danger ? 1 : 0.6 }}>{a.icon}</Box>
                                <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "inherit" }}>
                                    {a.label}
                                </Typography>
                            </Box>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}


function GuestCommentEditor({
    onSubmit, onCancel, sending,
    initialHtml = "", initialQuoted = false, initialAttachments = [], submitLabel = "Отправить",
}) {
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);

    const [quoted, setQuoted] = useState(initialQuoted);
    const [pending, setPending] = useState([]);
    const [kept, setKept]       = useState(initialAttachments);
    const [removed, setRemoved] = useState([]);
    const [error, setError]     = useState("");

    const pendingRef = useRef([]);
    pendingRef.current = pending;

    useEffect(() => {
        if (editorRef.current && initialHtml) editorRef.current.innerHTML = initialHtml;
    }, []);
    useEffect(() => () => {
        pendingRef.current.forEach(p => p.preview && URL.revokeObjectURL(p.preview));
    }, []);

    const addFiles = (fileList) => {
        const next = [];
        for (const f of Array.from(fileList || [])) {
            if (f.size > MAX_ATTACH_SIZE) { setError(`«${f.name}» больше 25 МБ — не прикрепил`); continue; }
            const isImage = (f.type || "").startsWith("image/");
            next.push({
                key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                file: f, isImage,
                preview: isImage ? URL.createObjectURL(f) : null,
            });
        }
        if (next.length) { setPending(p => [...p, ...next]); setError(""); }
    };
    const removePending = (key) => setPending(p => {
        const f = p.find(x => x.key === key);
        if (f?.preview) URL.revokeObjectURL(f.preview);
        return p.filter(x => x.key !== key);
    });
    const detachExisting = (id) => { setKept(k => k.filter(a => a.id !== id)); setRemoved(r => [...r, id]); };

    const insertTable = () => {
        editorRef.current?.focus();
        let html = "<table><tbody>";
        for (let r = 0; r < 2; r++) { html += "<tr>"; for (let c = 0; c < 2; c++) html += "<td><br></td>"; html += "</tr>"; }
        html += "</tbody></table><p><br></p>";
        document.execCommand("insertHTML", false, html);
    };
    const onPaste = (e) => {
        const items = e.clipboardData?.items || [];
        const imgs = [];
        for (const it of items) {
            if (it.kind === "file" && (it.type || "").startsWith("image/")) { const f = it.getAsFile(); if (f) imgs.push(f); }
        }
        if (imgs.length) { e.preventDefault(); addFiles(imgs); }
    };

    const submit = () => {
        if (sending) return;
        const html = editorRef.current?.innerHTML || "";
        const isEmpty = !html || html === "<br>" || html.replace(/<br\s*\/?>/gi, "").trim() === "";
        const hasAttach = pending.length > 0 || kept.length > 0;
        if (isEmpty && !hasAttach) return;
        onSubmit({ text: html, quoted, files: pending.map(p => p.file), removeAttachmentIds: removed });
        if (editorRef.current) editorRef.current.innerHTML = "";
        pending.forEach(p => p.preview && URL.revokeObjectURL(p.preview));
        setPending([]); setRemoved([]); setQuoted(false); setError("");
    };

    const tbBtn = { width: 26, height: 26, minWidth: 0, borderRadius: 7 };

    return (
        <Box sx={{ border: "1.5px solid", borderColor: "divider", borderRadius: 14, overflow: "hidden",
            "&:focus-within": { borderColor: "primary.300" }, transition: "border-color 0.15s" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.5, flexWrap: "wrap",
                bgcolor: "neutral.50", borderBottom: "1px solid", borderColor: "divider" }}>
                <Tooltip title="Жирный" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); document.execCommand("bold"); }}
                    ><FormatBoldRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <Tooltip title="Курсив" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); document.execCommand("italic"); }}
                    ><FormatItalicRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <Divider orientation="vertical" sx={{ mx: 0.5, height: 16 }} />
                <Tooltip title="Таблица 2×2" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); insertTable(); }}
                    ><TableChartRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <Tooltip title="Картинка" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); imageInputRef.current?.click(); }}
                    ><ImageRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <Tooltip title="Файл" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); fileInputRef.current?.click(); }}
                    ><AttachFileRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <Divider orientation="vertical" sx={{ mx: 0.5, height: 16 }} />
                <Tooltip title={quoted ? "Убрать цитату" : "Цитата"} size="sm">
                    <IconButton size="sm" variant={quoted ? "soft" : "plain"} color={quoted ? "primary" : "neutral"}
                        sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); setQuoted(v => !v); }}
                    ><FormatQuoteRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <input ref={imageInputRef} type="file" accept="image/*" multiple hidden
                    onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
                <input ref={fileInputRef} type="file" multiple hidden
                    onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
            </Box>

            <Box ref={editorRef} contentEditable suppressContentEditableWarning
                onPaste={onPaste}
                onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submit(); }}
                sx={{ px: 1.5, py: 1, minHeight: 50, outline: "none", fontSize: "0.87rem", lineHeight: 1.6,
                    color: "text.primary",
                    "& mark": { bgcolor: "#fef08a", borderRadius: "3px", px: "2px" },
                    "& table": { borderCollapse: "collapse", width: "100%", my: 0.5, fontSize: "0.82rem" },
                    "& td, & th": { border: "1px solid", borderColor: "neutral.300", p: "4px 8px", minWidth: 40 },
                    "&:empty:before": { content: '"Комментарий... (Ctrl+Enter)"', color: "#bbb", pointerEvents: "none" } }}
            />

            {(pending.length > 0 || kept.length > 0) && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, px: 1.5, py: 1,
                    borderTop: "1px solid", borderColor: "divider", bgcolor: "neutral.50" }}>
                    {kept.map(a => (
                        <GuestAttachChip key={a.id} kind={a.kind} src={a.kind === "image" ? mediaUrl(a.url) : null}
                            name={a.name} size={a.size} onRemove={() => detachExisting(a.id)} />
                    ))}
                    {pending.map(p => (
                        <GuestAttachChip key={p.key} kind={p.isImage ? "image" : "file"} src={p.preview}
                            name={p.file.name} size={p.file.size} onRemove={() => removePending(p.key)} />
                    ))}
                </Box>
            )}

            {error && (
                <Typography sx={{ px: 1.5, py: 0.5, fontSize: "0.75rem", color: "danger.500", bgcolor: "danger.50" }}>{error}</Typography>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 1.5, py: 0.5,
                bgcolor: "neutral.50", borderTop: "1px solid", borderColor: "divider" }}>
                {onCancel && (
                    <Button size="sm" variant="plain" color="neutral" onClick={onCancel}
                        sx={{ borderRadius: 9, height: 26, px: 1.2, fontSize: "0.79rem" }}>Отмена</Button>
                )}
                <Button size="sm" variant="solid" color="primary" disabled={sending}
                    startDecorator={sending
                        ? <CircularProgress size="sm" sx={{ "--CircularProgress-size": "12px" }} />
                        : <SendRoundedIcon sx={{ fontSize: 12 }} />}
                    onClick={submit} sx={{ borderRadius: 9, height: 26, px: 1.2, fontSize: "0.79rem" }}
                >{submitLabel}</Button>
            </Box>
        </Box>
    );
}

function GuestAttachChip({ kind, src, name, size, onRemove }) {
    return (
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 0.75,
            border: "1px solid", borderColor: "neutral.200", borderRadius: 10, p: 0.5, pr: 3,
            bgcolor: "background.surface", maxWidth: 200 }}>
            {kind === "image" && src ? (
                <Box component="img" src={src} alt={name}
                    sx={{ width: 34, height: 34, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
            ) : (
                <Box sx={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, display: "flex",
                    alignItems: "center", justifyContent: "center", bgcolor: "neutral.100" }}>
                    <InsertDriveFileRoundedIcon sx={{ fontSize: 18, color: "text.tertiary" }} />
                </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.74rem", fontWeight: 500, whiteSpace: "nowrap",
                    overflow: "hidden", textOverflow: "ellipsis" }}>{name}</Typography>
                {size ? <Typography sx={{ fontSize: "0.66rem", color: "text.tertiary" }}>{fmtSize(size)}</Typography> : null}
            </Box>
            <IconButton size="sm" variant="soft" color="danger"
                sx={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, minWidth: 0, borderRadius: 6 }}
                onClick={onRemove}
            ><CloseRoundedIcon sx={{ fontSize: 11 }} /></IconButton>
        </Box>
    );
}


// ─── GuestItemRow ─────────────────────────────────────────────────────────────

function GuestItemRow({
    item, index, depth = 0,
    accessLevel, shareToken,
    onPatch, onDelete, onAddSubtask,
    onCommentAdded, onCommentDeleted,
}) {
    const canEdit    = accessLevel === "edit";
    const canComment = accessLevel === "comment" || accessLevel === "edit";

    const [sending, setSending] = useState(false)
    const [commentOpen, setCommentOpen] = useState(false);
    const [viewer, setViewer] = useState(null); // { images, index } или null
    const isNew = !item.text;
    const [editing, setEditing]         = useState(isNew);
    const [editVal, setEditVal]         = useState(item.text);
    const inputRef = useRef(null);

    // Helper to get the actual <input> element inside MUI Joy Input wrapper
    const getInput = () => inputRef.current?.querySelector?.("input") ?? inputRef.current;

    // Focus when entering edit mode
    useEffect(() => {
        if (editing) {
            const raf = requestAnimationFrame(() => {
                getInput()?.focus();
            });
            return () => cancelAnimationFrame(raf);
        }
    }, [editing]);

    // Focus on mount if new item
    useEffect(() => {
        if (isNew) {
            const raf = requestAnimationFrame(() => {
                getInput()?.focus();
            });
            return () => cancelAnimationFrame(raf);
        }
    }, []);

    // Handle _startEdit flag from mobile menu
    useEffect(() => {
        if (item._startEdit) {
            setEditVal(item.text);
            setEditing(true);
            onPatch(item.id, { _startEdit: false });
        }
    }, [item._startEdit]);

    const flameColor = item.flame === "red" ? "#ef4444" : item.flame === "yellow" ? "#f59e0b" : null;

    // Патч на сервер через гостевой API
    const applyPatch = useCallback(async (id, patch) => {
        onPatch(id, patch); // оптимистично
        try {
            const api = makeGuestApi(shareToken);
            const body = {};
            if (patch.done      !== undefined) body.done        = patch.done;
            if (patch.text      !== undefined) body.text        = patch.text;
            if (patch.flame     !== undefined) body.flame       = patch.flame ?? "";
            if (patch.type      !== undefined) body.item_type   = patch.type;
            if (patch.expanded  !== undefined) body.is_expanded = patch.expanded;
            await api.patch(`/tasks/items/${id}/`, body);
        } catch (e) {
            console.error("Ошибка обновления задачи:", e);
        }
    }, [shareToken, onPatch]);

    const submitEdit = () => {
        if (!editVal.trim()) {
            // Пустая задача — удаляем
            onDelete(item.id);
            return;
        }
        if (editVal.trim() !== item.text) {
            applyPatch(item.id, { text: editVal.trim() });
        }
        setEditing(false);
    };

    const handleCommentSend = useCallback(async ({ text, quoted, files }) => {
        setSending(true);
        try {
            const api = makeGuestApi(shareToken);
            const fd = new FormData();
            fd.append("text", text);
            fd.append("is_quoted", quoted ? "true" : "false");
            (files || []).forEach(f => fd.append("files", f));
            const res = await api.post(`/tasks/items/${item.id}/comments/`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            onCommentAdded(item.id, normaliseComment(res.data));
            setCommentOpen(false);
        } catch (e) {
            console.error("Ошибка комментария:", e);
        } finally {
            setSending(false);
        }
    }, [shareToken, item.id, onCommentAdded]);

    const handleDeleteComment = useCallback(async (commentId) => {
        onCommentDeleted(item.id, commentId);
        try {
            const api = makeGuestApi(shareToken);
            await api.delete(`/tasks/comments/${commentId}/`);
        } catch (e) { console.error(e); }
    }, [shareToken, item.id, onCommentDeleted]);

    return (
        <Box sx={{ pl: depth > 0 ? `${Math.min(depth, 1) * 20}px` : 0 }}>
            <Box
                sx={{
                    display: "flex", alignItems: "center", gap: 0.5, px: 1.5, minHeight: 38,
                    borderRadius: 10,
                    "&:hover .item-actions": { opacity: 1 },
                    transition: "background 0.12s, box-shadow 0.3s",
                    ...(item.flame
                        ? flameRowSx(item.flame)
                        : { bgcolor: "transparent", "&:hover": { bgcolor: "neutral.softBg" } }),
                }}
            >
                {/* Subdir arrow for subtasks */}
                {depth > 0 && (
                    <SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 13, color: "text.tertiary", flexShrink: 0 }} />
                )}

                {/* Chevron для subtasks */}
                {(item.subtasks || []).length > 0 && (
                    <Box sx={{ width: 18, height: 18, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <motion.div
                            animate={{ rotate: item.expanded ? 90 : 0 }}
                            transition={{ duration: 0.15 }}
                            style={{ display: "flex", cursor: "pointer" }}
                            onClick={() => canEdit
                                ? applyPatch(item.id, { expanded: !item.expanded })
                                : onPatch(item.id, { expanded: !item.expanded })
                            }
                        >
                            <NavigateNextRoundedIcon sx={{ fontSize: 18, color: "text.tertiary" }} />
                        </motion.div>
                    </Box>
                )}

                {/* Иконка типа */}
                <ItemIcon
                    type={item.type}
                    done={item.done}
                    index={index}
                    canEdit={canEdit}
                    onToggle={() => applyPatch(item.id, { done: !item.done })}
                />

                {/* Текст / редактирование */}
                {editing ? (
                    <Box sx={{ flex: 1, display: "flex", gap: 0.5, alignItems: "center" }}>
                        <Input
                            ref={inputRef}
                            size="sm"
                            value={editVal}
                            onChange={e => setEditVal(e.target.value)}
                            onBlur={e => {
                                if (e.relatedTarget?.dataset?.action) return;
                                if (!editVal.trim()) onDelete(item.id);
                                else submitEdit();
                            }}
                            onKeyDown={e => {
                                if (e.key === "Enter") submitEdit();
                                if (e.key === "Escape") {
                                    if (isNew || !editVal.trim()) onDelete(item.id);
                                    else { setEditVal(item.text); setEditing(false); }
                                }
                            }}
                            sx={{ flex: 1, fontSize: "0.9rem", height: 30, "--Input-focusedHighlight": "transparent",
                                bgcolor: "transparent", py: 0, "& input": { lineHeight: 1.5 } }}
                            onClick={e => e.stopPropagation()}
                        />
                        <IconButton data-action="confirm" size="sm" variant="soft" color="primary"
                            sx={{ width: 30, height: 24, minWidth: 0, borderRadius: 10, flexShrink: 0 }}
                            onMouseDown={e => { e.preventDefault(); submitEdit(); }}
                        ><CheckRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                    </Box>
                ) : (
                    <Typography
                        sx={{
                            flex: 1, fontSize: "0.9rem", lineHeight: 1.5,
                            color: item.done ? "text.tertiary" : "text.primary",
                            textDecoration: item.done ? "line-through" : "none",
                            transition: "all 0.15s",
                            cursor: canEdit ? "text" : "default",
                        }}
                        onDoubleClick={canEdit ? () => { setEditVal(item.text); setEditing(true); } : undefined}
                    >
                        {item.text}
                    </Typography>
                )}

                {/* Счётчик подзадач */}
                {(item.subtasks || []).length > 0 && (
                    <Box sx={{
                        px: 0.75, py: 0.2, borderRadius: 6,
                        bgcolor: "neutral.100", fontSize: "0.7rem",
                        color: "text.tertiary", fontWeight: 600, flexShrink: 0,
                    }}>
                        {(item.subtasks || []).filter(s => s.done).length}/{(item.subtasks || []).length}
                    </Box>
                )}

                {/* Кнопки действий (edit / comment) */}
                <Box className="item-actions" sx={{
                    display: { xs: "none", md: "flex" }, gap: 0.25, flexShrink: 0,
                    opacity: 0, transition: "opacity 0.15s",
                    ".MuiBox-root:hover > &": { opacity: 1 },
                }}>
                    {/* Огонёк — только edit */}
                    {canEdit && (
                        <FlameMenu flame={item.flame} onChange={val => applyPatch(item.id, { flame: val })} />
                    )}

                    {/* Тип — только edit */}
                    {canEdit && (
                        <TypeMenu type={item.type} onChange={val => applyPatch(item.id, { type: val })} />
                    )}

                    {/* Подзадача — только edit, depth=0 */}
                    {canEdit && depth === 0 && (
                        <Tooltip title="Добавить подзадачу" size="sm">
                            <IconButton size="sm" variant="plain" color="neutral"
                                sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                onMouseDown={e => { e.stopPropagation(); onAddSubtask(item.id); }}
                            >
                                <SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Комментарий */}
                    {canComment && (
                        <Tooltip title="Комментарий" size="sm">
                            <IconButton size="sm" variant="plain"
                                color={commentOpen ? "primary" : "neutral"}
                                sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                onMouseDown={e => { e.stopPropagation(); setCommentOpen(v => !v); }}
                            >
                                <FormatQuoteRoundedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Удалить — только edit */}
                    {canEdit && (
                        <Tooltip title="Удалить задачу" size="sm">
                            <IconButton size="sm" variant="plain" color="danger"
                                sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                onMouseDown={e => {
                                    e.stopPropagation();
                                    if (window.confirm("Удалить задачу?")) onDelete(item.id);
                                }}
                            >
                                <DeleteRoundedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                <GuestItemMobileMenu
                    item={item}
                    accessLevel={accessLevel}
                    depth={depth}
                    onPatch={(id, patch) => { onPatch(id, patch); }}
                    onDelete={onDelete}
                    onAddSubtask={onAddSubtask}
                    onCommentToggle={() => setCommentOpen(v => !v)}
                />
            </Box>

            {/* Существующие комментарии — всегда видны если есть */}
            {(item.comments || []).length > 0 && (
                <Box sx={{ pl: "46px", pr: 1, pb: 0.5 }}>
                    {(item.comments || []).map(c => {
                        const images = (c.attachments || []).filter(a => a.kind === "image");
                        const files  = (c.attachments || []).filter(a => a.kind !== "image");
                        return (
                        <Box key={c.id} className="comment-bubble"
                            sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, my: 0.4 }}
                        >
                            <Box sx={{
                                flex: 1, px: 1.5, py: 0.7,
                                bgcolor: "primary.50", borderRadius: 10,
                                borderLeft: "3px solid", borderColor: "primary.400",
                            }}>
                                <Box component="div"
                                    sx={{ fontSize: "0.83rem", color: "text.secondary", lineHeight: 1.55,
                                        "& mark": { bgcolor: "#fef08a", borderRadius: "3px", px: "2px" },
                                        "& table": { borderCollapse: "collapse", width: "100%", my: 0.5, fontSize: "0.8rem" },
                                        "& td, & th": { border: "1px solid", borderColor: "neutral.300", p: "4px 8px" },
                                        "& img": { maxWidth: "100%", borderRadius: 8 } }}
                                    dangerouslySetInnerHTML={{ __html: c.text }}
                                />
                                {images.length > 0 && (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                                        {images.map((a, idx) => (
                                            <Box key={a.id}
                                                onClick={() => setViewer({
                                                    images: images.map(x => ({ url: mediaUrl(x.url), name: x.name })),
                                                    index: idx,
                                                })}
                                                sx={{ display: "block", lineHeight: 0, cursor: "zoom-in" }}>
                                                <Box component="img" src={mediaUrl(a.url)} alt={a.name}
                                                    sx={{ width: 92, height: 92, objectFit: "cover", borderRadius: 10,
                                                        border: "1px solid", borderColor: "neutral.200",
                                                        transition: "transform 0.15s", "&:hover": { transform: "scale(1.03)" } }} />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                {files.length > 0 && (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.75 }}>
                                        {files.map(a => (
                                            <Box key={a.id} component="a" href={mediaUrl(a.url)} target="_blank" rel="noreferrer" download
                                                sx={{ display: "flex", alignItems: "center", gap: 1, textDecoration: "none",
                                                    px: 1, py: 0.5, borderRadius: 9, bgcolor: "background.surface",
                                                    border: "1px solid", borderColor: "neutral.200", maxWidth: 320,
                                                    "&:hover": { borderColor: "primary.300" } }}>
                                                <InsertDriveFileRoundedIcon sx={{ fontSize: 18, color: "text.tertiary", flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography sx={{ fontSize: "0.78rem", color: "text.primary", whiteSpace: "nowrap",
                                                        overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</Typography>
                                                    {a.size ? <Typography sx={{ fontSize: "0.66rem", color: "text.tertiary" }}>{fmtSize(a.size)}</Typography> : null}
                                                </Box>
                                                <DownloadRoundedIcon sx={{ fontSize: 16, color: "text.tertiary", flexShrink: 0 }} />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                <Typography sx={{ fontSize: "0.7rem", color: "text.tertiary", mt: 0.3 }}>
                                    {c.authorName ? `${c.authorName} · ` : ""}{c.createdAt}
                                    {c.updatedAt ? " · изменено" : ""}
                                </Typography>
                            </Box>
                            {canEdit && (
                                <IconButton size="sm" variant="plain" color="danger"
                                    sx={{ width: 22, height: 22, minWidth: 0, borderRadius: 6, opacity: 0.4, "&:hover": { opacity: 1 }, mt: 0.5 }}
                                    onClick={() => handleDeleteComment(c.id)}
                                >
                                    <DeleteRoundedIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            )}
                        </Box>
                        );
                    })}
                </Box>
            )}

            {/* Поле ввода — только когда нажали кнопку комментария */}
            <AnimatePresence>
                {commentOpen && canComment && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }}
                        style={{ overflow: "hidden" }}
                    >
                        <Box sx={{ pl: "46px", pr: 1, pb: 1, pt: 0.5 }}>
                            <GuestCommentEditor
                                onSubmit={handleCommentSend}
                                onCancel={() => setCommentOpen(false)}
                            />
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Подзадачи */}
            <AnimatePresence>
                {item.expanded && (item.subtasks || []).length > 0 && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }}
                        style={{ overflow: "hidden" }}
                    >
                        {(item.subtasks || []).map((sub, si) => (
                            <GuestItemRow
                                key={sub._stableKey || sub.id} item={sub} index={si} depth={depth + 1}
                                accessLevel={accessLevel} shareToken={shareToken}
                                onPatch={onPatch} onDelete={onDelete} onAddSubtask={onAddSubtask}
                                onCommentAdded={onCommentAdded} onCommentDeleted={onCommentDeleted}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {viewer && (
                <ImageViewer images={viewer.images} index={viewer.index} onClose={() => setViewer(null)} />
            )}
        </Box>
    );
}

// ─── GuestSectionBlock ────────────────────────────────────────────────────────

function GuestSectionBlock({ section, accessLevel, shareToken, onSectionsChange, sections }) {
    const canEdit = accessLevel === "edit";
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleVal, setTitleVal]         = useState(section.title);
    const titleRef = useRef(null);

    useEffect(() => { if (editingTitle) titleRef.current?.focus(); }, [editingTitle]);

    // Обновление items в одном разделе
    const patchItems = useCallback((updater) => {
        onSectionsChange(prev => prev.map(s => s.id === section.id ? { ...s, items: updater(s.items) } : s));
    }, [section.id, onSectionsChange]);

    const handlePatch = useCallback((id, patch) => {
        patchItems(items => updateItemRec(items, id, it => ({ ...it, ...patch })));
    }, [patchItems]);

    const handleDelete = useCallback(async (id) => {
        patchItems(items => deleteItemRec(items, id));
        try {
            const api = makeGuestApi(shareToken);
            await api.delete(`/tasks/items/${id}/`);
        } catch (e) { console.error(e); }
    }, [patchItems, shareToken]);

    const handleAddSubtask = useCallback(async (parentId) => {
        const tmp = { id: genTmpId(), _stableKey: genStableKey(), type: "checkbox", text: "", done: false, flame: null, expanded: false, comments: [], subtasks: [] };
        patchItems(items => addSubtaskRec(items, parentId, tmp));
        try {
            const api = makeGuestApi(shareToken);
            const res = await api.post(`/tasks/items/${parentId}/subtasks/`, { text: "", item_type: "checkbox" });
            patchItems(items => replaceItemId(items, tmp.id, res.data.id));
        } catch (e) { console.error(e); }
    }, [patchItems, shareToken]);

    const handleAddItem = useCallback(async () => {
        const tmp = { id: genTmpId(), _stableKey: genStableKey(), type: "checkbox", text: "", done: false, flame: null, expanded: false, comments: [], subtasks: [] };
        patchItems(items => [...items, tmp]);
        try {
            const api = makeGuestApi(shareToken);
            const res = await api.post(`/tasks/sections/${section.id}/items/`, { text: "", item_type: "checkbox" });
            patchItems(items => replaceItemId(items, tmp.id, res.data.id));
        } catch (e) { console.error(e); }
    }, [patchItems, section.id, shareToken]);

    const handleCommentAdded = useCallback((itemId, comment) => {
        patchItems(items => updateItemRec(items, itemId, it => ({ ...it, comments: [...(it.comments || []), comment] })));
    }, [patchItems]);

    const handleCommentDeleted = useCallback((itemId, commentId) => {
        patchItems(items => updateItemRec(items, itemId, it => ({ ...it, comments: (it.comments || []).filter(c => c.id !== commentId) })));
    }, [patchItems]);

    const submitTitle = async () => {
        setEditingTitle(false);
        if (titleVal.trim() === section.title) return;
        onSectionsChange(prev => prev.map(s => s.id === section.id ? { ...s, title: titleVal.trim() } : s));
        try {
            const api = makeGuestApi(shareToken);
            await api.patch(`/tasks/sections/${section.id}/`, { title: titleVal.trim() });
        } catch (e) { console.error(e); }
    };

    const handleDeleteSection = async () => {
        if (!window.confirm("Удалить раздел вместе со всеми задачами?")) return;
        onSectionsChange(prev => prev.filter(s => s.id !== section.id));
        try {
            const api = makeGuestApi(shareToken);
            await api.delete(`/tasks/sections/${section.id}/`);
        } catch (e) { console.error(e); }
    };

    return (
        <Box sx={{ mb: 3 }}>
            {/* Заголовок раздела */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, pl: 1,
                "&:hover .sec-actions": { opacity: 1 } }}
            >
                {editingTitle && canEdit ? (
                    <Box sx={{ display: "flex", flex: 1, gap: 0.5, alignItems: "center" }}>
                        <Input
                            ref={titleRef}
                            size="sm" value={titleVal}
                            onChange={e => setTitleVal(e.target.value)}
                            onBlur={submitTitle}
                            onKeyDown={e => { if (e.key === "Enter") submitTitle(); if (e.key === "Escape") { setTitleVal(section.title); setEditingTitle(false); } }}
                            sx={{ flex: 1, fontSize: "0.78rem", fontWeight: 700, height: 28,
                                "--Input-focusedHighlight": "transparent", textTransform: "uppercase", letterSpacing: "0.09em" }}
                        />
                        <IconButton size="sm" variant="solid" color="success"
                            sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                            onMouseDown={submitTitle}
                        ><CheckRoundedIcon sx={{ fontSize: 13 }} /></IconButton>
                    </Box>
                ) : (
                    <>
                        <Typography sx={{
                            flex: 1, fontSize: "0.78rem", fontWeight: 700,
                            textTransform: "uppercase", letterSpacing: "0.09em",
                            color: "text.tertiary",
                            cursor: canEdit ? "text" : "default",
                        }}
                            onDoubleClick={canEdit ? () => { setTitleVal(section.title); setEditingTitle(true); } : undefined}
                        >
                            {section.title || (canEdit ? <Box component="span" sx={{ opacity: 0.4 }}>Раздел без названия</Box> : "")}
                        </Typography>

                        {canEdit && (
                            <Box className="sec-actions" sx={{ display: "flex", gap: 0.25, opacity: { xs: 0.4, md: 0 }, transition: "opacity 0.15s" }}>
                                <Tooltip title="Переименовать раздел" size="sm">
                                    <IconButton size="sm" variant="plain" color="neutral"
                                        sx={{ width: 22, height: 22, minWidth: 0, borderRadius: 6 }}
                                        onClick={() => { setTitleVal(section.title); setEditingTitle(true); }}
                                    ><EditRoundedIcon sx={{ fontSize: 13 }} /></IconButton>
                                </Tooltip>
                                <Tooltip title="Удалить раздел" size="sm">
                                    <IconButton size="sm" variant="plain" color="danger"
                                        sx={{ width: 22, height: 22, minWidth: 0, borderRadius: 6 }}
                                        onClick={handleDeleteSection}
                                    ><DeleteRoundedIcon sx={{ fontSize: 13 }} /></IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </>
                )}
            </Box>
            <Divider sx={{ mb: 1, opacity: 0.5 }} />

            {/* Задачи */}
            {(section.items || []).map((item, idx) => (
                <GuestItemRow
                    key={item._stableKey || item.id} item={item} index={idx}
                    accessLevel={accessLevel} shareToken={shareToken}
                    onPatch={handlePatch} onDelete={handleDelete} onAddSubtask={handleAddSubtask}
                    onCommentAdded={handleCommentAdded} onCommentDeleted={handleCommentDeleted}
                />
            ))}

            {/* Добавить задачу */}
            {canEdit && (
                <Box
                    onClick={handleAddItem}
                    sx={{
                        display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.5, mt: 0.5,
                        borderRadius: 10, cursor: "pointer", color: "text.tertiary",
                        "&:hover": { bgcolor: "primary.softBg", color: "primary.plainColor" },
                        transition: "all 0.15s",
                    }}
                >
                    <AddRoundedIcon sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "inherit" }}>
                        Добавить задачу
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

// ─── GuestTaskListView ────────────────────────────────────────────────────────

function GuestTaskListView({ initialTasklist, accessLevel, shareToken }) {
    const canEdit = accessLevel === "edit";

    const [tasklist, setTasklist] = useState(() => normaliseTaskList(initialTasklist));
    const [editingName, setEditingName] = useState(false);
    const [nameVal, setNameVal]         = useState(tasklist.name);
    const [refreshing, setRefreshing] = useState(false);
    const nameRef = useRef(null);

    useEffect(() => { if (editingName) nameRef.current?.focus(); }, [editingName]);

    const submitName = async () => {
        setEditingName(false);
        if (nameVal.trim() === tasklist.name) return;
        setTasklist(t => ({ ...t, name: nameVal.trim() }));
        try {
            const api = makeGuestApi(shareToken);
            await api.patch(`/tasks/lists/${tasklist.id}/`, { name: nameVal.trim() });
        } catch (e) { console.error(e); }
    };

    const handleAddSection = async () => {
        const tmp = { id: genTmpId(), title: "", items: [] };
        setTasklist(t => ({ ...t, sections: [...t.sections, tmp] }));
        try {
            const api = makeGuestApi(shareToken);
            const res = await api.post(`/tasks/lists/${tasklist.id}/sections/`, { title: "" });
            setTasklist(t => ({ ...t, sections: t.sections.map(s => s.id === tmp.id ? { ...s, id: res.data.id } : s) }));
        } catch (e) { console.error(e); }
    };

    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            const res = await APIGetShareData(shareToken);
            if (res?.data) {
                setTasklist(normaliseTaskList(res.data));
            }
        } catch (e) {
            console.error("Ошибка обновления:", e);
        } finally {
            setRefreshing(false);
        }
    }, [shareToken]);

    const allItems = tasklist.sections.flatMap(s => s.items || []);
    const countable = collectCountable(allItems);
    const total    = countable.length;
    const done     = countable.filter(i => i.done).length;
    const pct      = total > 0 ? Math.round((done / total) * 100) : 0;

    return (
        <Box>
            {/* Название */}
            {editingName ? (
                <Box sx={{ mb: 1 }}>
                    <Input
                        ref={nameRef}
                        size="lg" value={nameVal}
                        onChange={e => setNameVal(e.target.value)}
                        onBlur={e => { if (e.relatedTarget?.dataset?.titleAction) return; submitName(); }}
                        onKeyDown={e => { if (e.key === "Enter") submitName(); if (e.key === "Escape") { setNameVal(tasklist.name); setEditingName(false); } }}
                        sx={{ fontSize: "clamp(1.3rem, 3vw, 1.9rem)", fontWeight: 800, border: "none", boxShadow: "none",
                            bgcolor: "transparent", p: 0, "--Input-focusedHighlight": "transparent",
                            borderBottom: "2px solid var(--joy-palette-primary-200)", borderRadius: 0, mb: 1 }}
                    />
                    <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                        <Button size="sm" variant="soft" color="primary"
                            data-title-action="confirm"
                            onMouseDown={e => { e.preventDefault(); submitName(); }}
                            startDecorator={<CheckRoundedIcon sx={{ fontSize: 14 }} />}
                            sx={{ borderRadius: 10, height: 30, px: 1.5, fontSize: "0.78rem" }}
                        >Сохранить</Button>
                        <Button size="sm" variant="soft" color="neutral"
                            data-title-action="cancel"
                            onMouseDown={e => { e.preventDefault(); setNameVal(tasklist.name); setEditingName(false); }}
                            startDecorator={<CloseRoundedIcon sx={{ fontSize: 14 }} />}
                            sx={{ borderRadius: 10, height: 30, px: 1.5, fontSize: "0.78rem" }}
                        >Отмена</Button>
                    </Stack>
                </Box>
            ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <Typography
                        level="h3"
                        sx={{ fontWeight: 800, letterSpacing: "-0.5px",
                            cursor: canEdit ? "text" : "default", lineHeight: 1.1,
                            "&:hover": canEdit ? { color: "primary.plainColor" } : {},
                            transition: "color 0.15s",
                        }}
                        onClick={canEdit ? () => { setNameVal(tasklist.name); setEditingName(true); } : undefined}
                    >
                        {tasklist.name}
                    </Typography>
                    {canEdit && (
                        <IconButton
                            size="sm" variant="plain" color="neutral"
                            onClick={() => { setNameVal(tasklist.name); setEditingName(true); }}
                            sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 8, opacity: 0.45, flexShrink: 0 }}
                        >
                            <DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}

                    <Tooltip title="Обновить список" size="sm">
                        <IconButton
                            size="sm" variant="plain" color="neutral"
                            onClick={handleRefresh}
                            disabled={refreshing}
                            sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 8, opacity: 0.45,
                                flexShrink: 0, "&:hover": { opacity: 1 } }}
                        >
                            <motion.div
                                animate={{ rotate: refreshing ? 360 : 0 }}
                                transition={{ duration: 0.7, repeat: refreshing ? Infinity : 0, ease: "linear" }}
                                style={{ display: "flex" }}
                            >
                                <RefreshRoundedIcon sx={{ fontSize: 16 }} />
                            </motion.div>
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Прогресс */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Typography sx={{ color: "text.tertiary", fontSize: "0.85rem" }}>
                    {done} из {total} задач выполнено
                </Typography>
                <Box sx={{ width: 80, height: 5, bgcolor: "neutral.200", borderRadius: 4, overflow: "hidden" }}>
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        style={{ height: "100%", background: pct === 100 ? "#22c55e" : "#572AC3", borderRadius: 4 }}
                    />
                </Box>
                <Typography sx={{ fontSize: "0.8rem", color: "text.tertiary" }}>{pct}%</Typography>
            </Stack>

            {/* Разделы */}
            {tasklist.sections.map(sec => (
                <GuestSectionBlock
                    key={sec.id}
                    section={sec}
                    accessLevel={accessLevel}
                    shareToken={shareToken}
                    sections={tasklist.sections}
                    onSectionsChange={updater => setTasklist(t => ({ ...t, sections: updater(t.sections) }))}
                />
            ))}

            {/* Добавить раздел */}
            {canEdit && (
                <Box
                    onClick={handleAddSection}
                    sx={{
                        display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, mt: 1,
                        borderRadius: 10, cursor: "pointer", color: "text.tertiary",
                        border: "1.5px dashed", borderColor: "neutral.300",
                        "&:hover": { borderColor: "primary.300", bgcolor: "primary.softBg", color: "primary.plainColor" },
                        transition: "all 0.18s",
                    }}
                >
                    <AddRoundedIcon sx={{ fontSize: 17 }} />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "inherit" }}>
                        Добавить раздел
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

// ─── GuestFolderView ──────────────────────────────────────────────────────────

function GuestFolderView({ folder, accessLevel, shareToken }) {
    const [selectedList, setSelectedList] = useState(null);

    if (!folder) return null;

    if (selectedList) {
        return (
            <Box>
                <Button size="sm" variant="plain" color="neutral"
                    onClick={() => setSelectedList(null)}
                    sx={{ mb: 2, borderRadius: 10 }}
                >
                    ← Назад к папке
                </Button>
                <GuestTaskListView
                    initialTasklist={selectedList}
                    accessLevel={accessLevel}
                    shareToken={shareToken}
                />
            </Box>
        );
    }

    return (
        <Box>
            <Typography level="h3" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: "-0.5px" }}>
                {folder.name}
            </Typography>
            <Typography sx={{ color: "text.tertiary", fontSize: "0.85rem", mb: 3 }}>
                {(folder.children || []).length} списк(ов) задач
            </Typography>

            <Stack spacing={1.5}>
                {(folder.children || []).map(list => {
                    const allItems = (list.sections || []).flatMap(s => s.items || []);
                    const countable = collectCountable(allItems);
                    const total = countable.length;
                    const done  = countable.filter(i => i.done).length;
                    const pct   = total > 0 ? Math.round((done / total) * 100) : 0;

                    return (
                        <Sheet key={list.id}
                            onClick={() => setSelectedList(list)}
                            sx={{
                                p: 2, borderRadius: 16, cursor: "pointer",
                                border: "1.5px solid", borderColor: "neutral.200",
                                transition: "all 0.15s",
                                "&:hover": { borderColor: "primary.300", boxShadow: "sm", transform: "translateY(-1px)" },
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box sx={{
                                    width: 38, height: 38, borderRadius: 10,
                                    bgcolor: "primary.softBg",
                                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                }}>
                                    <AssignmentRoundedIcon sx={{ fontSize: 18, color: "primary.plainColor" }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }} noWrap>
                                        {list.name}
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                        {done}/{total} задач · {pct}%
                                    </Typography>
                                </Box>
                                <Box sx={{ width: 48, height: 5, bgcolor: "neutral.200", borderRadius: 4, overflow: "hidden" }}>
                                    <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: pct === 100 ? "success.400" : "primary.400", borderRadius: 4, transition: "width 0.4s" }} />
                                </Box>
                            </Stack>
                        </Sheet>
                    );
                })}
            </Stack>
        </Box>
    );
}

// ─── GuestTasksPage ───────────────────────────────────────────────────────────

export default function GuestTasksPage() {
    const { token } = useParams();

    const [loading,   setLoading]   = useState(true);
    const [error,     setError]     = useState(null);
    const [shareData, setShareData] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await APICheckShareLink(token);
            setShareData(res);
        } catch (e) {
            const msg = e?.response?.data?.error || "Ссылка недействительна или истекла";
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    // ── Загрузка ──────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Box sx={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2 }}>
                <CircularProgress size="md" />
                <Typography sx={{ color: "text.tertiary", fontSize: "0.9rem" }}>Проверяем доступ...</Typography>
            </Box>
        );
    }

    // ── Ошибка доступа ────────────────────────────────────────────────────────
    if (error) {
        return (
            <Box sx={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2, px: 3 }}>
                <Box sx={{ width: 64, height: 64, borderRadius: 16, bgcolor: "danger.softBg", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LockRoundedIcon sx={{ fontSize: 30, color: "danger.plainColor" }} />
                </Box>
                <Box sx={{ textAlign: "center" }}>
                    <Typography level="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Нет доступа</Typography>
                    <Typography sx={{ color: "text.tertiary", fontSize: "0.9rem" }}>{error}</Typography>
                </Box>
                <Button size="sm" variant="outlined" color="neutral" startDecorator={<RefreshRoundedIcon />} onClick={load}>
                    Попробовать снова
                </Button>
            </Box>
        );
    }

    const accessLevel = shareData?.access_level ?? "view";
    const targetType  = shareData?.target_type;
    const data        = shareData?.data;

    const isReadOnly  = accessLevel === "view";
    const canComment  = accessLevel === "comment" || accessLevel === "edit";
    const canEdit     = accessLevel === "edit";

    // Бейдж цвет/текст
    const badgeColor   = isReadOnly ? "neutral" : canEdit ? "success" : "primary";
    const badgeLabel   = isReadOnly ? "Только просмотр" : canEdit ? "Редактирование" : "Комментарии";
    const badgeBg      = isReadOnly ? "neutral.100"     : canEdit ? "success.softBg"  : "primary.softBg";
    const badgeBorder  = isReadOnly ? "neutral.200"     : canEdit ? "success.200"      : "primary.200";
    const badgeTextClr = isReadOnly ? "text.tertiary"   : canEdit ? "success.plainColor" : "primary.plainColor";

    return (
        <Box sx={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>

            {/* ── Шапка ── */}
            <Sheet sx={{
                position: "sticky", top: 0, zIndex: 50,
                px: 2, py: 1.25,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                borderBottom: "1px solid", borderColor: "neutral.200",
                bgcolor: "rgba(255,255,255,0.88)", backdropFilter: "blur(12px)",
            }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box component="img" src="/logo.png" sx={{
                        width: 32, height: 32, borderRadius: "10px",
                        boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
                    }} />
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.1 }}>
                            {data?.name || "Задачи"}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: "text.tertiary", lineHeight: 1 }}>
                            LunaDesk
                        </Typography>
                    </Box>
                </Stack>

                {/* Бейдж уровня доступа */}
                <Stack direction="row" alignItems="center" spacing={0.75}
                    sx={{ px: 1.25, py: 0.5, borderRadius: 20, bgcolor: badgeBg, border: "1px solid", borderColor: badgeBorder }}
                >
                    <LockRoundedIcon sx={{ fontSize: 12, color: badgeTextClr }} />
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: badgeTextClr }}>
                        {badgeLabel}
                    </Typography>
                </Stack>
            </Sheet>

            {/* ── Подсказка для режима комментирования ── */}
            {canComment && !canEdit && (
                <Box sx={{ bgcolor: "primary.softBg", borderBottom: "1px solid", borderColor: "primary.200", px: 3, py: 0.75 }}>
                    <Typography sx={{ fontSize: "0.8rem", color: "primary.plainColor", textAlign: "center" }}>
                        💬 Вы можете оставлять комментарии к задачам — наведите на задачу и нажмите <FormatQuoteRoundedIcon sx={{ fontSize: 13, verticalAlign: "middle" }} />
                    </Typography>
                </Box>
            )}

            {/* ── Подсказка для режима редактирования ── */}
            {canEdit && (
                <Box sx={{ bgcolor: "success.softBg", borderBottom: "1px solid", borderColor: "success.200", px: 3, py: 0.75 }}>
                    <Typography sx={{ fontSize: "0.8rem", color: "success.plainColor", textAlign: "center" }}>
                        ✏️ Режим редактирования — вы можете изменять задачи, добавлять разделы и комментарии
                    </Typography>
                </Box>
            )}

            {/* ── Контент ── */}
            <Box sx={{ flex: 1, overflow: "auto", px: { xs: 2, md: 6 }, py: 3, maxWidth: 860, mx: "auto", width: "100%" }}>
                {targetType === "folder" ? (
                    <GuestFolderView folder={data} accessLevel={accessLevel} shareToken={token} />
                ) : (
                    <GuestTaskListView initialTasklist={data} accessLevel={accessLevel} shareToken={token} />
                )}
            </Box>

            {/* ── Подпись ── */}
            <Box sx={{ textAlign: "center", py: 2, opacity: 0.4 }}>
                <Stack sx={{ m: 1, mb: 1 }} justifyContent="center" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1.2}>
                        <Box component="img" src="/logo.png" sx={{ width: 28, height: 28, borderRadius: "8px", boxShadow: "0 2px 8px rgba(99,102,241,0.25)" }} />
                        <Stack justifyContent="center" alignItems="flex-start">
                            <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", lineHeight: 1.1, letterSpacing: "-0.3px", color: "text.primary" }}>
                                LunaDesk
                            </Typography>
                            <Typography level="body-xs" sx={{ color: "text.tertiary", lineHeight: 1 }}>
                                Персональный помощник
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
}